import { useCallback, useEffect, useState } from "react";
import {
  INDICATOR_DRAFT_VAZIO,
  type IndicatorDefinitionResponse,
  type IndicatorDraft,
  type IndicatorValueResponse,
} from "@/types/indicators.types";
import type { useIndicatorPreview } from "./hooks";

/** Últimos 30 dias — a janela em que o gestor confere se a fórmula está dizendo o que ele espera. */
function periodo() {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 86_400_000);
  return { from: from.toISOString(), to: to.toISOString() };
}

/**
 * Spec 082 (FR-029) — o rascunho da fórmula, com o valor recalculando a cada frase.
 *
 * Salvar NÃO é o que faz o número aparecer: enquanto a definição tiver um termo completo de cada
 * lado, o valor é pedido ao servidor. É isso que permite ao gestor descobrir que errou a frase
 * antes de a definição existir.
 */
export function useIndicatorDraft(preview: ReturnType<typeof useIndicatorPreview>) {
  const [draft, setDraftState] = useState<IndicatorDraft>(INDICATOR_DRAFT_VAZIO);
  const [value, setValue] = useState<IndicatorValueResponse | null>(null);

  const setDraft = useCallback((patch: Partial<IndicatorDraft>) => {
    setDraftState((d) => ({ ...d, ...patch }));
  }, []);

  const load = useCallback((item: IndicatorDefinitionResponse) => {
    setDraftState({
      id: item.id,
      name: item.name,
      baseUnit: item.baseUnit,
      target: item.target ?? undefined,
      terms: item.terms,
    });
    setValue(null);
  }, []);

  const reset = useCallback(() => {
    setDraftState(INDICATOR_DRAFT_VAZIO);
    setValue(null);
  }, []);

  const completos = draft.terms.filter((t) => t.lineId && t.productId);
  const temNumerador = completos.some((t) => t.side === "NUMERATOR");
  const temDenominador = completos.some((t) => t.side === "DENOMINATOR");
  const assinatura = JSON.stringify(completos);

  const podeCalcular = temNumerador && temDenominador;

  const { mutateAsync } = preview;
  useEffect(() => {
    // Fórmula incompleta não pede cálculo. O valor exibido é DERIVADO de `podeCalcular` no
    // retorno, e não zerado aqui: escrever estado dentro do efeito para dizer "ainda não dá"
    // dispara uma renderização em cascata para uma informação que já está no rascunho.
    if (!podeCalcular) return;
    let cancelado = false;
    void mutateAsync({
      definition: {
        name: draft.name || "rascunho",
        baseUnit: draft.baseUnit || "un",
        target: draft.target ?? null,
        terms: completos,
      },
      ...periodo(),
    })
      .then((v) => {
        if (!cancelado) setValue(v);
      })
      .catch(() => {
        // Falha de cálculo não derruba o editor: o card mostra "sem valor" e o gestor segue
        // montando. O toast de erro global já avisou.
        if (!cancelado) setValue(null);
      });
    return () => {
      cancelado = true;
    };
    // `assinatura` é a lista de frases serializada: é ela que precisa disparar o recálculo, e não
    // a identidade do array, que muda a cada tecla digitada no nome.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recalcula pela assinatura das frases
  }, [assinatura, podeCalcular, mutateAsync]);

  return {
    draft,
    setDraft,
    load,
    reset,
    value: podeCalcular ? value : null,
    isCalculating: preview.isPending,
  };
}
